
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ClipboardCheck,
  HardHat,
  Wrench,
  Search,
  FileText,
  PenTool,
  Settings,
  ShieldCheck,
  Building2,
  Lightbulb,
  Monitor,
  Camera,
  ParkingCircle,
  AlertTriangle,
} from 'lucide-react';

import './ConstructionSupport.css';

const scope = [
  {
    Icon: ClipboardCheck,
    title: '現地確認・ご提案',
    description:
      '設置場所や周辺環境、電源・配線条件などを確認し、用途に合った仕様や施工方法を検討します。',
  },
  {
    Icon: HardHat,
    title: '製作・施工',
    description:
      '看板・LED設備・取付金物など、現場条件に応じた製作・施工に対応します。',
  },
  {
    Icon: Wrench,
    title: '施工後のサポート',
    description:
      '設置後の点検・修理・交換・追加設置についてもご相談いただけます。',
  },
];

const flow = [
  {
    id: 'consultation',
    number: '01',
    Icon: ClipboardCheck,
    title: 'お問い合わせ・ご相談',
    description:
      '設置予定場所や用途、ご希望の内容などをお伺いします。',
  },
  {
    id: 'survey',
    number: '02',
    Icon: Search,
    title: '現地確認・ヒアリング',
    description:
      '設置環境、取付方法、寸法、電源・配線経路などを確認します。必要に応じて計測や写真撮影を行います。',
  },
  {
    id: 'proposal',
    number: '03',
    Icon: FileText,
    title: 'ご提案・お見積り',
    description:
      '確認した条件をもとに、仕様や施工内容を整理し、お見積りをご案内します。',
  },
  {
    id: 'design',
    number: '04',
    Icon: PenTool,
    title: '設計・製作',
    description:
      'ご発注内容に応じて、必要な設計・製品手配・製作を進めます。',
  },
  {
    id: 'construction',
    number: '05',
    Icon: HardHat,
    title: '施工・設置',
    description:
      '施工日程を調整し、安全と現場環境に配慮しながら設置・配線・調整を行います。',
  },
  {
    id: 'support',
    number: '06',
    Icon: Settings,
    title: '施工後のサポート',
    description:
      '設置後の動作確認や必要なご説明を行います。点検・修理・交換などもご相談いただけます。',
  },
];

const services = [
  {
    Icon: Building2,
    title: '看板・サイン',
    description: '店舗看板、施設サイン、案内表示など',
    path: '/products/signage',
  },
  {
    Icon: Lightbulb,
    title: 'LED照明',
    description: '店舗・施設・工場などの照明設備',
    path: '/products/led-lighting',
  },
  {
    Icon: Monitor,
    title: 'LEDビジョン・電光掲示板',
    description: '映像表示設備、案内・注意喚起表示',
    path: '/products/led-vision',
  },
  {
    Icon: Camera,
    title: '防犯カメラ',
    description: '店舗・施設・駐車場などの監視設備',
    path: '/products/security-camera',
  },
  {
    Icon: ParkingCircle,
    title: 'パーキングシステム',
    description: '駐車場案内表示、満空表示など',
    path: '/products/parking-system',
  },
  {
    Icon: AlertTriangle,
    title: '緊急出動警報板',
    description: '緊急車両出動時の注意喚起設備',
    path: '/products/emergency-board',
  },
  {
    Icon: Wrench,
    title: 'カスタム金物製作',
    description: '取付架台、ブラケット、各種取付金物',
    path: '/products/custom-metal',
  },
];

const maintenance = [
  '設備の点検・状態確認',
  '故障・不具合に関するご相談',
  '部品交換・既設設備の改修',
  '設備の追加設置・更新',
];

const faq = [
  {
    question: '現場調査は必須ですか？',
    answer:
      '設置内容や現場条件によって異なります。取付場所や電源・配線経路の確認が必要な場合は、現地調査をご案内します。',
  },
  {
    question: '見積りだけでも依頼できますか？',
    answer:
      'はい。設置予定場所やご希望の内容をお知らせください。現地調査や詳細設計が必要な場合の費用については、事前にご案内します。',
  },
  {
    question: '施工期間はどのくらいですか？',
    answer:
      '製品の種類、製作内容、設置環境、工事規模によって異なります。具体的な工程や施工日程は、お見積り時にご案内します。',
  },
  {
    question: '保証期間はありますか？',
    answer:
      '保証の有無や期間、対象範囲は製品および施工内容によって異なります。個別のお見積り・ご契約内容に基づいてご案内します。',
  },
  {
    question: '既存設備の修理や交換も対応していますか？',
    answer:
      '既設の看板・LED設備・防犯カメラなどについてもご相談いただけます。設備の状態、機種、部品の供給状況などを確認して対応可否をご案内します。',
  },
];

const ConstructionSupport = () => {
  return (
    <div className="cs-page">

      {/* ===== HERO ===== */}
      <section className="cs-hero">
        <div className="cs-container">
          <span className="cs-eyebrow">
            CONSTRUCTION &amp; SUPPORT
          </span>

          <h1>施工・サポート</h1>

          <p>
            現地確認から製作・施工、設置後のご相談まで。
            <br />
            設置環境や用途に合わせて対応します。
          </p>
        </div>
      </section>

      {/* ===== SERVICE SCOPE ===== */}
      <section className="cs-section" id="service-scope">
        <div className="cs-container">
          <div className="cs-section-head">
            <span className="cs-eyebrow">SERVICE SCOPE</span>
            <h2>HSの対応範囲</h2>
            <p>
              製品の選定だけでなく、現場の条件を確認しながら、
              必要な施工内容をご提案します。
            </p>
          </div>

          <div className="cs-scope-grid">
            {scope.map((item) => {
              const Icon = item.Icon;

              return (
                <article className="cs-scope-card" key={item.title}>
                  <div className="cs-scope-icon">
                    <Icon size={23} strokeWidth={1.6} />
                  </div>

                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="cs-section cs-section-alt">
        <div className="cs-container">
          <div className="cs-section-head">
            <span className="cs-eyebrow">PROCESS</span>
            <h2>ご相談から施工までの流れ</h2>
            <p>
              お問い合わせ後、内容を確認しながら
              各工程を進めていきます。
            </p>
          </div>

          <div className="cs-flow-grid">
            {flow.map((item) => {
              const Icon = item.Icon;

              return (
                <article
                  className="cs-flow-card"
                  key={item.id}
                  id={item.id}
                >
                  <div className="cs-flow-top">
                    <span className="cs-flow-number">
                      {item.number}
                    </span>
                    <Icon size={20} strokeWidth={1.6} />
                  </div>

                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              );
            })}
          </div>

          <p className="cs-process-note">
            ※案件の内容や設置条件によって、工程や進め方は異なります。
          </p>
        </div>
      </section>

      {/* ===== CONSTRUCTION SERVICES ===== */}
      <section className="cs-section" id="services">
        <div className="cs-container">
          <div className="cs-section-head">
            <span className="cs-eyebrow">CONSTRUCTION</span>
            <h2>主な施工対応内容</h2>
            <p>
              各種看板・LED設備をはじめ、
              幅広い設備についてご相談いただけます。
            </p>
          </div>

          <div className="cs-service-grid">
            {services.map((service) => {
              const Icon = service.Icon;

              return (
                <Link
                  to={service.path}
                  className="cs-service-item"
                  key={service.title}
                >
                  <Icon size={20} strokeWidth={1.6} />

                  <div className="cs-service-text">
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                  </div>

                  <ArrowRight size={16} strokeWidth={1.7} />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== AFTER SUPPORT ===== */}
      <section className="cs-section cs-section-alt" id="after-support">
        <div className="cs-container">
          <div className="cs-support-layout">
            <div className="cs-support-intro">
              <span className="cs-eyebrow">AFTER SUPPORT</span>
              <h2>施工後のサポート</h2>

              <p>
                設置後の設備についても、
                点検・修理・交換・追加設置などの
                ご相談を承ります。
              </p>

              <p>
                対応内容は設備の状態や設置条件を確認のうえ、
                個別にご案内します。
              </p>
            </div>

            <div className="cs-maintenance-list">
              {maintenance.map((item) => (
                <div className="cs-maintenance-item" key={item}>
                  <ShieldCheck size={19} strokeWidth={1.6} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== WORKS LINK ===== */}
      <section className="cs-works-section">
        <div className="cs-container">
          <div className="cs-works-inner">
            <div>
              <span className="cs-eyebrow">WORKS</span>
              <h2>施工実績</h2>
              <p>
                看板・サイン、LED表示設備など、
                これまでの施工事例をご紹介しています。
              </p>
            </div>

            <Link to="/works" className="cs-works-link">
              施工実績一覧を見る
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="cs-section cs-section-alt" id="faq">
        <div className="cs-container">
          <div className="cs-section-head">
            <span className="cs-eyebrow">FAQ</span>
            <h2>よくあるご質問</h2>
          </div>

          <div className="cs-faq-list">
            {faq.map((item) => (
              <details className="cs-faq-item" key={item.question}>
                <summary>
                  <span className="cs-faq-q">Q</span>
                  <span>{item.question}</span>
                </summary>

                <div className="cs-faq-answer">
                  <span className="cs-faq-a">A</span>
                  <p>{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section className="cs-contact">
        <div className="cs-container">
          <div className="cs-contact-inner">
            <div>
              <span className="cs-eyebrow">CONTACT</span>

              <h2>施工・設備に関するご相談</h2>

              <p>
                設置場所やご希望の内容をお知らせください。
                お問い合わせ内容を確認のうえ、ご案内します。
              </p>
            </div>

            <Link to="/contact" className="cs-contact-button">
              お問い合わせ
              <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ConstructionSupport;
